import {
  UsersPermissionsUserInput,
  useUpdateUsersPermissionsUserMutation,
  useUsersPermissionsUserQuery,
} from "@/generated";
import { useRouter } from "next/router";
import { useCallback } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import WButton from "@/components/wbutton/WButton";
import WTextInput from "@/components/wtext-input/WTextInput";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

const ReactQuill =
  typeof window === "object" ? require("react-quill") : () => false;

interface IProps {
  onCancel: () => void;
}

export const WProfileEditAboutMeForm = ({ onCancel }: IProps) => {
  const router = useRouter();
  const userId = router.query["id"]?.toString();
  const { t } = useTranslation("profileEdit");

  const { data: userAttributes, refetch } = useUsersPermissionsUserQuery({
    fetchPolicy: "cache-and-network",
    skip: !userId,
    variables: { id: userId ?? "" },
  });

  const userData = userAttributes?.usersPermissionsUser?.data?.attributes;

  const [updateProfile, { loading }] = useUpdateUsersPermissionsUserMutation();

  const {
    register,
    setValue,
    watch,
    formState: { errors, isValid },
    handleSubmit,
  } = useForm<UsersPermissionsUserInput>({
    mode: "all",
    defaultValues: {},
  });

  const onSubmit: SubmitHandler<UsersPermissionsUserInput> = useCallback(
    (data) => {
      if (userId) {
        updateProfile({
          onCompleted: () => toast.success(t("saved-profile")),
          onError: () => toast.error(t("saved-profile-error")),
          variables: { updateUsersPermissionsUserId: userId, data },
        }).then(() => {
          refetch();
          onCancel();
        });
      }
    },
    [onCancel, refetch, t, updateProfile, userId]
  );

  const modules = {
    toolbar: [
      [{ header: [1, 2, false] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
    ],
  };

  const aboutValue = watch("about");

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="w-full">
      <div className="flex flex-col w-full gap-5">
        <div className="overflow-hidden">
          {document && (
            <ReactQuill
              theme="snow"
              value={aboutValue}
              className="bg-white rounded-md"
              onChange={(value: string) => {
                setValue("about", value);
              }}
              modules={modules}
            />
          )}
        </div>
        <WTextInput
          {...register("city")}
          placeholder={t("city")?.toString()}
          error={errors?.city}
        />
      </div>
      <div className="flex flex-col w-full gap-5 mt-5">
        <WButton
          text={t("save-changes")}
          buttonType="submit"
          loading={loading}
          disabled={!isValid}
          type="primary"
        />
        <WButton text={t("cancel")} type="secondary" onClick={onCancel} />
      </div>
    </form>
  );
};

export default WProfileEditAboutMeForm;
